import { useEffect, useState } from "react";
import type { WardrobeModule } from "../types";
import { getShelvingGridLayout, resizeShelvingOpening } from "../domain/shelving";
import { usePlannerStore } from "../store/usePlannerStore";

function OpeningField({ label, value, maximum, disabled, onCommit }: {
  label: string; value: number; maximum: number; disabled?: boolean; onCommit: (value: number) => void;
}) {
  const [draft, setDraft] = useState(String(Math.round(value)));
  useEffect(() => setDraft(String(Math.round(value))), [value]);
  const commit = () => {
    const number = Number(draft);
    if (!draft.trim() || !Number.isFinite(number)) { setDraft(String(Math.round(value))); return; }
    const next = Math.max(100, Math.min(maximum, number));
    setDraft(String(Math.round(next)));
    if (Math.abs(next - value) >= 0.5) onCommit(next);
  };
  return <label>{label}<input type="number" min={100} max={Math.floor(maximum)} step={1} value={draft}
    disabled={disabled}
    onChange={e => setDraft(e.target.value)} onBlur={commit}
    onKeyDown={e => { if (e.key === "Enter") e.currentTarget.blur(); }} /></label>;
}

/**
 * Точные размеры отдельных проёмов внутри уже заданного габарита.
 *
 * Готовые схемы жили здесь же (mode="templates"), пока не переехали в витрину:
 * теперь конструкция Egel — такая же модель, как работа дизайнера, и добавляется
 * в комнату отдельным изделием, а не перестраивает текущее.
 */
export function ShelvingDesignTools({ module }: { module: WardrobeModule }) {
  const update = usePlannerStore(state => state.updateModule);
  const grid = getShelvingGridLayout(module);

  return <details className="sl-designs">
    <summary>Точные размеры отдельных проёмов</summary>
    <p className="sl-help">Общий габарит остаётся прежним. Если изменить один проём, свободное место распределится между соседними. Ряды считаются снизу от пола.</p>
    {(["column", "row"] as const).map(axis => {
      const sizes = axis === "column" ? grid.cellWidths : grid.cellHeights;
      const total = sizes.reduce((a, b) => a + b, 0);
      return <div key={axis} className="sl-opening-fields">{sizes.map((size, index) => <OpeningField
        key={`${module.id}:${axis}:${index}`} label={`${axis === "column" ? "Колонка" : "Ряд"} ${index + 1}`}
        value={size} maximum={total - 100 * (sizes.length - 1)} disabled={sizes.length < 2} onCommit={value => {
          const ratios = resizeShelvingOpening(sizes, index, value);
          if (ratios) update(module.id, axis === "column" ? { shelvingColumnRatios: ratios } : { shelvingRowRatios: ratios });
        }} />)}</div>;
    })}
  </details>;
}
